import { useCurrentPlan } from 'hooks/useCurrentPlan'
import Styles from './useAnnualTrialDiscountBanner.module.scss'
import { pluralize } from 'utils'
import { Button } from 'blaze-ui/Button'
import { Close } from 'blaze-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { AlmanacFileSummary, UserRole, Workspace } from 'types'
import { useFiles } from 'hooks/queries/finder/useFiles'
import { Modal, useModals } from 'almanac-ui/Modal'
import { useButton } from '@react-aria/button'
import { useRef, useState } from 'react'
import { PaymentPlan, PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { usePaymentPlans } from 'hooks/queries/usePaymentPlans'
import { useUserRole } from 'hooks/useUserRole'
import { useMutation } from 'react-query'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import dayjs from 'dayjs'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { SuccessModal } from 'components/AnnualUpgrade/AnnualUpgrade'
import queryClient from 'queryClient'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { usePaywall } from 'components/TimeTriggerPaywall/hooks/usePaywall'
import { useAuth } from 'hooks/UseAuth'
import { useAPMobileWeb } from 'hooks/useAPMobileWeb'

const PERCENT = 33

const UpgradeModal = ({
  workspace,
  plan,
  onConfirm,
}: {
  workspace: Workspace
  plan: PaymentPlan
  onConfirm: () => void
}) => {
  const { openModal, closeAllModals } = useModals()
  const monthlyPrice = plan.price.month.unit_amount
  const oldAnnualPrice = monthlyPrice * 12
  const newAnnualPrice = Math.round(oldAnnualPrice * ((100 - PERCENT) / 100))

  const { mutate: updateSubscription, isLoading: isUpdating } = useMutation(
    async ({ workspaceId, priceId }: { workspaceId: number; priceId: string }) =>
      WorkspaceClient.updateSubscription(workspaceId, priceId),
    {
      onSuccess: (res) => {
        if (res.data.success) {
          queryClient.invalidateQueries([workspace.id, 'current-plan'])
          const nextInvoiceDate = dayjs(res.data.plan_details?.next_payment?.due_date).format('MMMM D, YYYY')
          showToastMessage("You've upgraded to annual plan!", 'pink')
          openModal(SuccessModal, { onCancel: () => closeAllModals(), nextInvoiceDate }, { replace: true })
        } else {
          showToastMessage('We were unable to upgrade your plan', 'error')
        }
      },
      onError: () => {
        showToastMessage('We were unable to upgrade your plan', 'error')
      },
    },
  )

  const upgradeToAnnualPlan = async () => {
    updateSubscription({ workspaceId: workspace.id, priceId: plan.price.year.price_id })
  }

  return (
    <Modal.Root bodyClassName={Styles.modal} size="sm">
      <Modal.Header
        title="Get 33% OFF Annual Pro Plan Trial-Stage Discount"
        headingLevel={1}
        variant="hero"
        heroImageAlt=""
        heroImage="https://res.cloudinary.com/almanac/image/upload/v1731509756/blaze_assets/annual_upgrade/heroes_posing_nzocha.png"
      />
      <Modal.Content>
        <div className={Styles.modalContent}>
          Your workspace "{workspace.name}" is currently at the ${monthlyPrice} Pro Plan billed monthly. If you switch
          to annual billing, we'll discount your annual payment with 33%{' '}
          <span className={Styles.bold}>resulting in 4 free months!</span>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={closeAllModals}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.annualPrice}>
            <span className={Styles.oldPrice}>${oldAnnualPrice}/yr</span>${newAnnualPrice}/yr
          </div>
          <Button isDisabled={isUpdating} size="lg" onPress={upgradeToAnnualPlan}>
            {isUpdating ? 'Upgrading...' : 'Confirm'}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

export const useAnnualTrialDiscountBanner = () => {
  const annualTrialDiscountEnabled = useFlipperFlag('annual_trial_discount')
  const { openModal } = useModals()
  const userRole = useUserRole()
  const [localDismissed, setLocalDismissed] = useState(false)
  const { data: paymentPlans, isLoading: plansLoading } = usePaymentPlans()
  const { data: currentPlan, isLoading: planLoading } = useCurrentPlan()
  const workspace = useCurrentWorkspace()
  const auth = useAuth()
  const { data: recentFiles, isLoading: loadingRecentFiles } = useFiles<AlmanacFileSummary>(
    {
      location: 'recents_home_new',
      workspaceId: workspace?.id ?? -1,
      query: { items: 1, skip_projects: true },
    },
    {
      enabled: !workspace?.is_virtual_marketer,
    },
  )
  const { openPaywall } = usePaywall()
  const isNewRegistration = useFlipperFlag('new_registration')

  const isAdmin = userRole === UserRole.ADMIN
  const proPlan = paymentPlans?.payment_plans?.find((p) => p.key === PaymentPlanKey.BlazePro)

  const handleOpenModal = () => {
    if (workspace && currentPlan && proPlan && isAdmin) {
      if (isNewRegistration) {
        openPaywall()
      } else {
        openModal(UpgradeModal, { workspace, plan: proPlan, onConfirm: () => null })
      }
    }
  }

  const { isMobile, isIOSNative } = useResponsiveDesign()
  const isAPMobileWeb = useAPMobileWeb()
  const buttonRef = useRef(null)
  const { buttonProps } = useButton({ onPress: handleOpenModal, elementType: 'div' }, buttonRef)

  const isLoading = plansLoading || planLoading || loadingRecentFiles
  const trialDaysLeft = currentPlan?.trial_days_left ?? -1
  const dismissalKey = `annual-trial-discount-banner-dismissed-${trialDaysLeft <= 2 ? 'last-minute' : 'normal'}`

  const dismissed = localDismissed || !!localStorage.getItem(dismissalKey)
  const numFiles = recentFiles?.pages?.[0]?.data?.files?.length ?? 0
  const hasFiles = isNewRegistration ? true : numFiles > 0
  const validPlan =
    isAdmin && currentPlan && currentPlan.plan === PaymentPlanKey.BlazePro && currentPlan.billing_frequency === 'month'

  const showBanner =
    annualTrialDiscountEnabled &&
    !isMobile &&
    !isIOSNative &&
    !isAPMobileWeb &&
    !isLoading &&
    hasFiles &&
    !dismissed &&
    trialDaysLeft >= 0 &&
    (isNewRegistration ? auth?.onboarded : true) &&
    validPlan &&
    !workspace?.is_virtual_marketer

  const annualTrialDiscountBanner = (
    <div className={Styles.bannerContainer}>
      <div ref={buttonRef} {...buttonProps} className={Styles.banner}>
        <div>
          {trialDaysLeft === 0 ? 'Expires today!' : `Expires in ${trialDaysLeft} ${pluralize('day', trialDaysLeft)}!`}
        </div>
        <div>
          GET
          <span className={Styles.oldPercent}>25%</span>
          33% OFF Annual Pro Plan Trial-Stage Discount!
        </div>

        <div />
      </div>

      <div className={Styles.close}>
        <Button
          aria-label="Dismiss Banner"
          onPress={() => {
            localStorage.setItem(dismissalKey, 'true')
            setLocalDismissed(true)
          }}
          variant="tertiary"
          size="xs"
        >
          <Close size={16} color="#ffffff" />
        </Button>
      </div>
    </div>
  )

  return {
    showAnnualTrialDiscountBanner: showBanner,
    annualTrialDiscountBanner: showBanner && annualTrialDiscountBanner,
  }
}
